import { createContext, useContext } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useFtue } from './useFtue.js';
import type { UseFtueResult } from './useFtue.js';

const FtueContext = createContext<UseFtueResult | null>(null);

export interface FtueProviderProps {
  children: ReactNode;
}

/**
 * Single shared `useFtue()` instance for the app (F9) — mirrors `WsProvider`/
 * `AuthProvider`'s "one instance, read via context" shape. Needed because
 * TWO call sites must observe/drive the SAME tour state: the root-mounted
 * `<FtueTour>` overlay, and the `/settings/notifications` page's "Replay
 * tutorial" entry point (F9: "re-runnable from Settings") — two independent
 * `useFtue()` calls would each own separate state and `replay()` from
 * Settings would never reach the tour actually rendered at the root.
 *
 * WIRING (see `./WIRING.md`): mount `<FtueProvider>` once near the app root
 * (inside `<AuthProvider>`/`<WsProvider>`, since `useFtue` needs both), and
 * render `<FtueTour />` once anywhere inside it.
 */
export function FtueProvider({ children }: FtueProviderProps): ReactElement {
  const value = useFtue();
  return <FtueContext.Provider value={value}>{children}</FtueContext.Provider>;
}

export function useFtueContext(): UseFtueResult {
  const ctx = useContext(FtueContext);
  if (!ctx) throw new Error('useFtueContext must be used within an <FtueProvider>');
  return ctx;
}
